import { APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { UrlBuilder } from 'http-url-builder';
import { SingletonSecureStorageService } from './core/services/singleton-secure-storage.service';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CoreModule } from './core/core.module';
import { AppComponent } from './app.component';
import {
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthorizationService } from './core/services/authorization.service';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { SyncfusionDropDownListPatchService } from '@core/services/syncfusion-dropdown-list-patch.service';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientid,
      authority: environment.authority,
      redirectUri: environment.redirecturl,
      knownAuthorities: environment.knownAuthorities,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback: (logLevel, message) => {
          console.log(message);
        },
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.authScopes,
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    'https://graph.microsoft.com/v1.0',
    environment.authScopes
  );
  protectedResourceMap.set(
    new UrlBuilder(environment.apim.baseUrl).build(),
    environment.authScopes
  );
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    CoreModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) =>
          new TranslateHttpLoader(
            http,
            './assets/i18n/',
            `.json?v=${new Date().getTime()}`
          ),
        deps: [HttpClient],
      },
    }),
    MsalModule.forRoot(
      MSALInstanceFactory(),
      MSALGuardConfigFactory(),
      MSALInterceptorConfigFactory()
    ),
  ],
  providers: [
    SingletonSecureStorageService,
    {
      provide: APP_INITIALIZER,
      useFactory: (ss: SingletonSecureStorageService) => () => ss.init(),
      deps: [SingletonSecureStorageService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (sd: SyncfusionDropDownListPatchService) => () => sd.init(),
      deps: [SyncfusionDropDownListPatchService],
      multi: true,
    },
    AuthorizationService,
    {
      provide: APP_INITIALIZER,
      useFactory: (as: AuthorizationService) => () => as.checkAuth(),
      deps: [AuthorizationService],
      multi: true,
    },
    MsalGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
